import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  FileText, Plus, Send, Download, Link2, Trash2, Edit, Eye,
  Loader2, Clock, CheckCircle, XCircle, Mail, Search, Copy
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-gray-500/20 text-gray-400", icon: FileText },
  sent: { label: "Sent", color: "bg-blue-500/20 text-blue-400", icon: Send },
  viewed: { label: "Viewed", color: "bg-purple-500/20 text-purple-400", icon: Eye },
  accepted: { label: "Accepted", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-500/20 text-red-400", icon: XCircle },
};

const emptyForm = {
  title: "", recipient_name: "", recipient_email: "", recipient_company: "",
  recipient_phone: "", items: [{ description: "", quantity: 1, unit_price: 0, discount: 0 }],
  notes: "", payment_terms: "", valid_until: "", tax_rate: 0, custom_intro: "",
};

const AdminProposals = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [viewTimeline, setViewTimeline] = useState(null);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => { fetchProposals(); }, [filterStatus]);

  const fetchProposals = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== "all" ? `?status=${filterStatus}` : "";
      const res = await axios.get(`${API}/proposals${params}`, getAuthConfig());
      setProposals(res.data.proposals || []);
    } catch (e) {
      console.error("Failed to fetch proposals:", e);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, items: [{ description: "", quantity: 1, unit_price: 0, discount: 0 }] });
    setShowForm(true);
  };

  const openEdit = async (p) => {
    setEditingId(p.proposal_id);
    setForm({
      title: p.title || "",
      recipient_name: p.recipient_name || "",
      recipient_email: p.recipient_email || "",
      recipient_company: p.recipient_company || "",
      recipient_phone: p.recipient_phone || "",
      items: p.items?.length ? p.items : [{ description: "", quantity: 1, unit_price: 0, discount: 0 }],
      notes: p.notes || "",
      payment_terms: p.payment_terms || "",
      valid_until: p.valid_until ? p.valid_until.slice(0, 10) : "",
      tax_rate: p.tax_rate || 0,
      custom_intro: p.custom_intro || "",
    });
    setShowForm(true);
  };

  const saveProposal = async () => {
    if (!form.title || !form.recipient_name || !form.recipient_email) {
      toast.error("Title, recipient name, and email are required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/proposals/${editingId}`, form, getAuthConfig());
        toast.success("Proposal updated");
      } else {
        await axios.post(`${API}/proposals`, form, getAuthConfig());
        toast.success("Proposal created");
      }
      setShowForm(false);
      fetchProposals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const deleteProposal = async (id) => {
    if (!confirm("Delete this proposal?")) return;
    try {
      await axios.delete(`${API}/proposals/${id}`, getAuthConfig());
      toast.success("Proposal deleted");
      fetchProposals();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const sendProposal = async (id) => {
    setSendingId(id);
    try {
      await axios.post(`${API}/proposals/${id}/send`, {}, getAuthConfig());
      toast.success("Proposal sent!");
      fetchProposals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSendingId(null);
    }
  };

  const copyPublicLink = (token) => {
    const url = `${window.location.origin}/proposal/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied!");
  };

  const openPdf = (token) => {
    window.open(`${API}/proposals/public/${token}/pdf`, "_blank");
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0, discount: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const calcTotal = () => {
    let sub = 0, disc = 0;
    (form.items || []).forEach(i => {
      const line = (i.quantity || 0) * (i.unit_price || 0);
      sub += line;
      disc += line * ((i.discount || 0) / 100);
    });
    const afterDisc = sub - disc;
    const tax = afterDisc * ((form.tax_rate || 0) / 100);
    return { subtotal: sub, discount: disc, tax, total: afterDisc + tax };
  };

  const filtered = proposals.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.title || "").toLowerCase().includes(q) ||
      (p.recipient_name || "").toLowerCase().includes(q) ||
      (p.recipient_email || "").toLowerCase().includes(q);
  });

  const totals = calcTotal();
  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="admin-proposals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">Custom Package Proposals</h1>
          <p className="text-muted-foreground">Create, send, and track sales proposals</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0046BE] text-white" data-testid="create-proposal-btn">
          <Plus className="w-4 h-4 mr-2" /> New Proposal
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search proposals..."
            className="bg-theme-surface border-theme text-foreground pl-9"
            data-testid="proposals-search-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-theme-surface border-theme text-foreground" data-testid="proposals-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover border-theme">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="viewed">Viewed</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const count = proposals.filter(p => p.status === key).length;
          const Icon = cfg.icon;
          return (
            <Card key={key} className="glass-card border-theme cursor-pointer hover:border-muted-foreground transition-all" onClick={() => setFilterStatus(key)}>
              <CardContent className="p-3 flex items-center gap-3">
                <Icon className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">{cfg.label}</p>
                  <p className="text-lg font-bold text-foreground">{count}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Proposals Table */}
      <Card className="glass-card border-theme">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="proposals-table">
              <thead>
                <tr className="border-b border-theme">
                  <th className="text-left text-muted-foreground text-sm py-3 px-4">Title</th>
                  <th className="text-left text-muted-foreground text-sm py-3 px-4">Recipient</th>
                  <th className="text-left text-muted-foreground text-sm py-3 px-4">Total</th>
                  <th className="text-left text-muted-foreground text-sm py-3 px-4">Status</th>
                  <th className="text-left text-muted-foreground text-sm py-3 px-4">Date</th>
                  <th className="text-right text-muted-foreground text-sm py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.draft;
                  return (
                    <tr key={p.proposal_id} className="border-b border-theme hover:bg-muted/50" data-testid={`proposal-row-${p.proposal_id}`}>
                      <td className="py-3 px-4">
                        <p className="text-foreground font-medium">{p.title}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-foreground text-sm">{p.recipient_name}</p>
                        <p className="text-muted-foreground text-xs">{p.recipient_email}</p>
                      </td>
                      <td className="py-3 px-4 text-foreground font-medium">{fmt(p.total)}</td>
                      <td className="py-3 px-4">
                        <Badge className={sc.color}>{sc.label}</Badge>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground text-sm">
                        {p.created_at ? format(new Date(p.created_at), "MMM dd, yyyy") : "—"}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => openEdit(p)} title="Edit" data-testid={`edit-proposal-${p.proposal_id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => sendProposal(p.proposal_id)} title="Send Email" disabled={sendingId === p.proposal_id} data-testid={`send-proposal-${p.proposal_id}`}>
                            {sendingId === p.proposal_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => openPdf(p.public_token)} title="Download PDF" data-testid={`pdf-proposal-${p.proposal_id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => copyPublicLink(p.public_token)} title="Copy Link" data-testid={`copy-link-${p.proposal_id}`}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => setViewTimeline(p)} title="Timeline" data-testid={`timeline-proposal-${p.proposal_id}`}>
                            <Clock className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => deleteProposal(p.proposal_id)} title="Delete" data-testid={`delete-proposal-${p.proposal_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!loading && filtered.length === 0 && (
              <p className="text-center text-muted-foreground py-12">No proposals found</p>
            )}
            {loading && <p className="text-center text-muted-foreground py-12">Loading...</p>}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-background border-theme max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">{editingId ? "Edit Proposal" : "New Proposal"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-muted-foreground text-sm">Proposal Title *</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Premium Service Package" className="bg-theme-surface border-theme text-foreground mt-1" data-testid="proposal-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-muted-foreground text-sm">Recipient Name *</label>
                <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                  placeholder="John Doe" className="bg-theme-surface border-theme text-foreground mt-1" data-testid="proposal-recipient-name" />
              </div>
              <div>
                <label className="text-muted-foreground text-sm">Recipient Email *</label>
                <Input value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                  placeholder="john@company.com" className="bg-theme-surface border-theme text-foreground mt-1" data-testid="proposal-recipient-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-muted-foreground text-sm">Company</label>
                <Input value={form.recipient_company} onChange={(e) => setForm({ ...form, recipient_company: e.target.value })}
                  placeholder="Company name" className="bg-theme-surface border-theme text-foreground mt-1" />
              </div>
              <div>
                <label className="text-muted-foreground text-sm">Phone</label>
                <Input value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })}
                  placeholder="Phone number" className="bg-theme-surface border-theme text-foreground mt-1" />
              </div>
            </div>
            <div>
              <label className="text-muted-foreground text-sm">Custom Intro Message</label>
              <Textarea value={form.custom_intro} onChange={(e) => setForm({ ...form, custom_intro: e.target.value })}
                placeholder="A personalized message for the recipient..." rows={2}
                className="bg-theme-surface border-theme text-foreground mt-1" />
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-muted-foreground text-sm font-medium">Line Items</label>
                <Button variant="ghost" size="sm" onClick={addItem} className="text-blue-400 h-7" data-testid="add-line-item-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`line-item-${idx}`}>
                    <div className="col-span-5">
                      {idx === 0 && <span className="text-muted-foreground text-xs">Description</span>}
                      <Input value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)}
                        placeholder="Service description" className="bg-theme-surface border-theme text-foreground text-sm" />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <span className="text-muted-foreground text-xs">Qty</span>}
                      <Input type="number" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)}
                        className="bg-theme-surface border-theme text-foreground text-sm" />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <span className="text-muted-foreground text-xs">Price</span>}
                      <Input type="number" value={item.unit_price} onChange={(e) => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)}
                        className="bg-theme-surface border-theme text-foreground text-sm" />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <span className="text-muted-foreground text-xs">Disc %</span>}
                      <Input type="number" value={item.discount} onChange={(e) => updateItem(idx, "discount", parseFloat(e.target.value) || 0)}
                        className="bg-theme-surface border-theme text-foreground text-sm" />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {form.items.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => removeItem(idx)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals Preview */}
            <div className="bg-muted/30 rounded-lg p-3 border border-theme space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground">{fmt(totals.subtotal)}</span>
              </div>
              {totals.discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount</span>
                  <span className="text-red-400">-{fmt(totals.discount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Tax</span>
                  <Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })}
                    className="w-16 h-6 text-xs bg-theme-surface border-theme text-foreground" placeholder="%" />
                  <span className="text-muted-foreground text-xs">%</span>
                </div>
                <span className="text-foreground">{fmt(totals.tax)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t border-theme pt-1">
                <span className="text-foreground">Total</span>
                <span className="text-foreground">{fmt(totals.total)}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-muted-foreground text-sm">Valid Until</label>
                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  className="bg-theme-surface border-theme text-foreground mt-1" />
              </div>
              <div>
                <label className="text-muted-foreground text-sm">Payment Terms</label>
                <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                  placeholder="Net 30, Due on receipt..." className="bg-theme-surface border-theme text-foreground mt-1" />
              </div>
            </div>
            <div>
              <label className="text-muted-foreground text-sm">Notes</label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional notes or terms..." rows={3}
                className="bg-theme-surface border-theme text-foreground mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-foreground">Cancel</Button>
            <Button onClick={saveProposal} className="bg-[#0046BE] text-white" disabled={saving} data-testid="save-proposal-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              {saving ? "Saving..." : editingId ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog open={!!viewTimeline} onOpenChange={() => setViewTimeline(null)}>
        <DialogContent className="bg-background border-theme max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Proposal Timeline</DialogTitle>
          </DialogHeader>
          {viewTimeline && (
            <div className="space-y-1">
              <p className="text-foreground font-medium mb-3">{viewTimeline.title}</p>
              <div className="space-y-3">
                {(viewTimeline.timeline || []).map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-400 mt-2 shrink-0" />
                    <div>
                      <p className="text-foreground text-sm font-medium capitalize">{t.action}</p>
                      <p className="text-muted-foreground text-xs">
                        {t.at ? format(new Date(t.at), "MMM dd, yyyy h:mm a") : ""}
                        {t.by ? ` by ${t.by}` : ""}
                        {t.to ? ` to ${t.to}` : ""}
                        {t.reason ? ` — "${t.reason}"` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProposals;
